import type { AtomManifest } from './zeroTTSProfile';

const MANIFEST_BUCKET = 'zerotts-manifests';

export function sanitize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9_-]/g, '_');
}

/**
 * Stage 26.6: the atom cache bucket is now scoped by voice fingerprint so
 * repinning (or switching between single and blend mode) produces a fresh
 * bucket — the prior pin's atoms are NOT reused at a different voice. When
 * `voiceFingerprint` is omitted the function returns the Stage-26 name;
 * this preserves read access to manifests written before 26.6.
 */
export function atomBucketName(
  personaSeed: string,
  voiceFingerprint?: string,
): string {
  const base = `zerotts-atoms-${sanitize(personaSeed)}`;
  if (!voiceFingerprint) return base;
  return `${base}-${sanitize(voiceFingerprint)}`;
}

export function manifestKey(personaSeed: string): string {
  return `/manifest/${sanitize(personaSeed)}`;
}

/**
 * Stage 26.6: remove atom buckets for this persona whose fingerprint does
 * NOT match the keeper. Called before/after install so stale pin buckets
 * don't accumulate in Cache Storage. The shared manifest is left alone —
 * it is overwritten, not pruned.
 */
export async function pruneStalePersonaBuckets(
  personaSeed: string,
  keepFingerprint: string | null,
): Promise<void> {
  if (typeof caches === 'undefined') return;
  const keep = keepFingerprint
    ? atomBucketName(personaSeed, keepFingerprint)
    : null;
  const legacyKeep = atomBucketName(personaSeed);
  const prefix = `zerotts-atoms-${sanitize(personaSeed)}`;
  const keys = await caches.keys();
  for (const key of keys) {
    // Only touch buckets for THIS persona.
    if (!key.startsWith(prefix)) continue;
    // Keep the currently-active fingerprint bucket.
    if (keep && key === keep) continue;
    // Keep the Stage-26 legacy bucket only when no fingerprint is active.
    if (!keep && key === legacyKeep) continue;
    await caches.delete(key);
  }
}

export async function readManifest(
  personaSeed: string,
): Promise<AtomManifest | null> {
  if (typeof caches === 'undefined') return null;
  const cache = await caches.open(MANIFEST_BUCKET);
  const res = await cache.match(manifestKey(personaSeed));
  if (!res) return null;
  try {
    return (await res.json()) as AtomManifest;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[zerotts] manifest parse failed for "${personaSeed}":`, err);
    return null;
  }
}

export async function writeManifest(
  personaSeed: string,
  manifest: AtomManifest,
): Promise<void> {
  if (typeof caches === 'undefined') return;
  const cache = await caches.open(MANIFEST_BUCKET);
  await cache.put(
    manifestKey(personaSeed),
    new Response(JSON.stringify(manifest), {
      headers: { 'Content-Type': 'application/json' },
    }),
  );
}

export async function clearPersonaCache(personaSeed: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  // Stage 26.6: remove every fingerprint-scoped atom bucket this persona
  // has accumulated plus the Stage-26 legacy bucket.
  await pruneStalePersonaBuckets(personaSeed, null);
  // pruneStalePersonaBuckets preserves the legacy bucket when no keeper is
  // set; remove it explicitly here so clearPersonaCache is truly empty.
  await caches.delete(atomBucketName(personaSeed));
  const mCache = await caches.open(MANIFEST_BUCKET);
  await mCache.delete(manifestKey(personaSeed));
}

export async function clearAllZeroTTSCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  const keys = await caches.keys();
  for (const key of keys) {
    if (key.startsWith('zerotts-atoms-') || key === MANIFEST_BUCKET) {
      await caches.delete(key);
    }
  }
}

/** Rough byte footprint for a manifest — sums AtomEntry.byte_size. */
export function manifestCacheBytes(manifest: AtomManifest | null): number {
  if (!manifest) return 0;
  let total = 0;
  for (const entry of Object.values(manifest.atoms)) {
    total += entry.byte_size ?? 0;
  }
  return total;
}
